// Sending email THROUGH APOLLO (2026-10-20) — the browser half of
// terraform enrich/apollo_send.py, reached at /api/enrich/apollo-*.
//
// The browser never talks to Apollo and holds no key. It hands the server the
// drafts it already shows (recipient + subject + body), now or for a time, and
// reads back a ledger row per email: scheduled → queued → sent (or failed with
// Apollo's own reason), then opens/clicks once Apollo reports them. The mail
// goes out from the mailbox of the Apollo user the workspace's key acts as —
// `apolloSetup()` names it so the dialog can say so before anything is sent.
//
// A draft pressed twice is refused server-side (the send id hashes the draft),
// so a double click or a retried batch can never mail one person twice.
import { requireIdToken } from './cognito.js'

const FALLBACK_BASE = 'https://hzpio22kcl.execute-api.us-east-1.amazonaws.com/prod'

async function authHeaders(extra = {}) {
  const token = await requireIdToken()
  return { ...extra, ...(token ? { Authorization: `Bearer ${token}` } : {}) }
}

// Same-origin first; local dev has no /api proxy (the history fallback answers
// with index.html), so a non-JSON answer retries against the deployed API with
// the same token — exactly enrich.js#apiFetch.
async function apiFetch(pathAndQuery, init = {}) {
  let res = await fetch(`/api/enrich/${pathAndQuery}`, init)
  if (!(res.headers.get('content-type') || '').includes('application/json')) {
    res = await fetch(`${FALLBACK_BASE}/enrich/${pathAndQuery}`, init)
  }
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) {
    const err = new Error(`The send API answered ${res.status}`)
    err.status = res.status
    throw err
  }
  const data = await res.json()
  if (!res.ok) {
    const err = new Error(data.error || `The send API answered ${res.status}`)
    err.status = res.status
    throw err
  }
  return data
}

// ── setup: is a key there, does Apollo take it, who is the sender ──────────
let setupCache = null // { at, promise }
const SETUP_TTL_MS = 5 * 60 * 1000

/** { configured, ok, sender:{name,email}, accounts:[…], error } — never throws. */
export function apolloSetup({ fresh = false } = {}) {
  if (!fresh && setupCache && Date.now() - setupCache.at < SETUP_TTL_MS) return setupCache.promise
  const promise = (async () => {
    try {
      return { available: true, ...(await apiFetch('apollo-setup', { headers: await authHeaders() })) }
    } catch (err) {
      return { available: false, configured: false, ok: false, accounts: [], error: err.message }
    }
  })()
  setupCache = { at: Date.now(), promise }
  promise.then((s) => {
    // A failed check is not worth remembering for five minutes.
    if (!s.ok && setupCache?.promise === promise) setupCache = null
  })
  return promise
}

/** The Apollo mailbox the mail leaves from, or null. */
export function senderMailbox(setup) {
  const accounts = (setup?.accounts || []).filter((a) => a.active && a.mine)
  return accounts.find((a) => a.default) || accounts[0] || null
}

// ── the ledger store: every send this tab knows, by draft key ───────────────
const sends = new Map() // id -> send
const listeners = new Set()
const notify = () => listeners.forEach((fn) => fn())

export function subscribeApolloSends(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function remember(list) {
  for (const s of list || []) if (s && s.id) sends.set(s.id, { ...sends.get(s.id), ...s })
  notify()
  schedulePoll()
}

/** The newest send of one draft (draft_key), or null. */
export function sendForDraft(draftKey) {
  if (!draftKey) return null
  let best = null
  for (const s of sends.values()) {
    if (s.draft_key === draftKey && (!best || String(s.created_at) > String(best.created_at))) best = s
  }
  return best
}

export const PENDING = new Set(['scheduled', 'sending', 'queued'])
export const isPending = (s) => Boolean(s && PENDING.has(s.status))

/**
 * Queue drafts. `emails`: [{ to, to_name, to_title, company, website,
 * lead_key, kind, draft_key, subject, body_html, body_text }]; `sendAt` an
 * ISO time or null for now. Resolves { batch_id, sends, refused, due_at };
 * throws with the server's words on a refusal.
 */
export async function sendViaApollo({ emails, sendAt = null }) {
  const data = await apiFetch('apollo-send', {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ emails, send_at: sendAt || null }),
  })
  remember([...(data.sends || []), ...(data.refused || [])])
  return data
}

export async function cancelApolloSend(id) {
  const data = await apiFetch('apollo-cancel', {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ id }),
  })
  remember([data.send])
  return data.send
}

/** The caller's ledger (newest first). Also refreshes the store. */
export async function listApolloSends({ batch = '', lead = '', scope = '' } = {}) {
  const q = new URLSearchParams()
  if (batch) q.set('batch', batch)
  if (lead) q.set('lead', lead)
  if (scope) q.set('scope', scope)
  const data = await apiFetch(`apollo-sends${q.toString() ? `?${q}` : ''}`, { headers: await authHeaders() })
  remember(data.sends)
  return data.sends || []
}

let loaded = null
/** Load the ledger once per tab so cards remember what already went. */
export function loadApolloSendsOnce() {
  if (!loaded) loaded = listApolloSends().catch(() => (loaded = null))
  return loaded
}

// While anything this tab sent is still pending, re-read the ledger every 20 s
// so a card moves from Scheduled → Sent (or Failed) without a reload.
let pollTimer = null
const POLL_MS = 20_000
function schedulePoll() {
  if (pollTimer) return
  const anyPending = [...sends.values()].some(isPending)
  if (!anyPending) return
  pollTimer = setTimeout(async () => {
    pollTimer = null
    try {
      await listApolloSends()
    } catch {
      schedulePoll()
    }
  }, POLL_MS)
}

// ── scheduling helpers ──────────────────────────────────────────────────────

/** "2026-10-21T09:00" (a datetime-local value) for the next weekday 9:00 AM local. */
export function nextMorningLocal(now = new Date()) {
  const d = new Date(now)
  d.setDate(d.getDate() + 1)
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1)
  d.setHours(9, 0, 0, 0)
  return toLocalInput(d)
}

export function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** A datetime-local value as an ISO instant, or '' when it isn't one. */
export function localInputToIso(value) {
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString()
}

/** "Tue, Oct 21, 9:00 AM" in the viewer's own zone. */
export function whenLabel(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** One line for a send's state, for the chip on a card. */
export function statusLabel(s) {
  if (!s) return ''
  switch (s.status) {
    case 'scheduled':
      return s.scheduled ? `Scheduled · ${whenLabel(s.due_at)}` : 'Sending…'
    case 'sending':
      return 'Sending…'
    case 'queued':
      return 'Sent to Apollo'
    case 'sent': {
      const bits = ['Sent via Apollo']
      if (s.bounced) bits.push('bounced')
      else if (s.opens) bits.push(`${s.opens} open${s.opens === 1 ? '' : 's'}`)
      if (s.replied) bits.push('replied')
      return bits.join(' · ')
    }
    case 'failed':
      return `Not sent — ${s.failure_reason || 'Apollo refused it'}`
    case 'canceled':
      return 'Canceled'
    default:
      return s.status || ''
  }
}

/** Test seam: forget everything this module cached. */
export function _resetApolloSend() {
  sends.clear()
  setupCache = null
  loaded = null
  clearTimeout(pollTimer)
  pollTimer = null
}
